import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useOnlineStatus } from "@/offline/online-status";

/**
 * Selection mode context. The provider owns the *counters* and the *mode*
 * flags; the page that owns the items owns the actual selection — the records
 * view its `selectedIds` / `selectedRecords`, the Issues page its set of issue
 * ids — and publishes it indirectly via `setSelectedCount` / `setIsAllSelected`
 * plus the action-registration callbacks.
 *
 * The records view registers the six callbacks (Delete, Report, Issues,
 * Share, SelectAll, UnselectAll) on mount, the Issues page the three its bar
 * can show (Report, SelectAll, UnselectAll); the bulk-action bar — rendered
 * inside BottomNav at the layout level — invokes them via the matching
 * `trigger*` functions. This decouples the bottom-nav surface from each page's
 * state without lifting item arrays into context (where they would be touched
 * by every render of the layout, not just the page that owns them).
 *
 * See [[selection-mode.spec]] for the behavior contract.
 */

type SelectionModeContextValue = {
  isSelectionMode: boolean;
  /**
   * The session is for a report: the bar shows Cancel · count · Report and the
   * header shows no Cancel of its own. Derived, not stored — true on Photos
   * with `?isReport=true` (the legacy `?mode=report` is honored for one
   * release) and on the Issues list always, since a report is the only thing
   * issues are selected for. See [[selection-mode.spec]] § Report mode.
   */
  isReportMode: boolean;
  /**
   * The session was started from the Reports page (New Report → Photos or
   * Issues, which add `?isReport=true`), so its Cancel returns there.
   */
  startedFromReports: boolean;
  selectedCount: number;
  /**
   * Everything the owning view shows is selected — flips the FAB to "Unselect
   * All". Published by the view, which knows what "everything" means: every
   * loaded photo, or every issue in a fully loaded issues view.
   */
  isAllSelected: boolean;
  /** Select All is still loading the rest of the view (Issues only). */
  isSelectingAll: boolean;
  isDeletingRecords: boolean;
  isSharing: boolean;

  enterSelectionMode: () => void;
  exitSelectionMode: () => void;
  /**
   * End a report-mode session: back to the Reports page when it was started
   * there, otherwise just `exitSelectionMode()`. The bar's report-mode Cancel
   * and the photos dialog's full-exit close both call it.
   */
  exitReportMode: () => void;
  setSelectedCount: (n: number) => void;
  setIsAllSelected: (v: boolean) => void;
  setIsSelectingAll: (v: boolean) => void;
  setIsDeletingRecords: (v: boolean) => void;

  registerDeleteAction: (fn: () => void) => void;
  registerReportAction: (fn: () => void) => void;
  registerIssuesAction: (fn: () => void) => void;
  registerShareAction: (fn: () => Promise<void>) => void;
  registerSelectAllAction: (fn: () => void) => void;
  registerUnselectAllAction: (fn: () => void) => void;

  triggerDelete: () => void;
  triggerReport: () => void;
  triggerIssues: () => void;
  triggerShare: () => void;
  triggerSelectAll: () => void;
  triggerUnselectAll: () => void;
};

const SelectionModeContext = createContext<SelectionModeContextValue | null>(
  null,
);

interface SelectionModeProviderProps {
  children: React.ReactNode;
}

export function SelectionModeProvider({ children }: SelectionModeProviderProps) {
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [isDeletingRecords, setIsDeletingRecords] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Action callbacks live in refs so registration doesn't trigger re-renders
  // of the bottom-nav action bar every time the records view re-renders.
  const deleteRef = useRef<(() => void) | null>(null);
  const reportRef = useRef<(() => void) | null>(null);
  const issuesRef = useRef<(() => void) | null>(null);
  const shareRef = useRef<(() => Promise<void>) | null>(null);
  const selectAllRef = useRef<(() => void) | null>(null);
  const unselectAllRef = useRef<(() => void) | null>(null);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  // useSearch with strict:false because the project layout straddles
  // multiple child routes; only the records and issues pages declare the
  // report param. We read `isReport`/`mode` and ignore everything else.
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  // `?isReport=true` is the canonical param; `?mode=report` is honored for
  // one release as back-compat for saved deep links.
  const isReportSearch =
    search.isReport === true ||
    search.isReport === "true" ||
    search.mode === "report";
  const isOnline = useOnlineStatus();

  const enterSelectionMode = useCallback(() => {
    // Selection mode is unavailable offline — none of its actions
    // (delete, generate report) can run without the network. See
    // offline-availability.md.
    if (!isOnline) return;
    setIsSelectionMode(true);
  }, [isOnline]);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedCount(0);
    setIsAllSelected(false);
    setIsSelectingAll(false);
  }, []);

  // Single URL-sync effect — replaces an earlier two-effect design that
  // raced (child mount enters report mode, parent pathname-watcher resets
  // it because effects fire bottom-up = parent last).
  //
  // Why a single effect: when both the deep-link entry and the auto-reset
  // live in the same hook, there's no "child wins / parent wins" race —
  // the same effect decides what selection state should be for the
  // current URL.
  //
  //   - `?isReport=true` in the URL (while online) → enter selection mode
  //   - any other URL, or being offline → reset to the inactive default
  //
  // `isReportMode` is derived from the same URL param (computed below),
  // not stored — so a deep-link entry only needs to toggle `isSelectionMode`
  // here. Selections set by user interaction change `selectedCount`/
  // `selectedIds` but NOT pathname/search, so this effect doesn't re-fire
  // and won't clobber the user's work.
  //
  // The `isOnline` dep is the deliberate exception: selection mode is
  // unavailable offline, so when connectivity drops the effect re-fires
  // and falls through to the reset branch — auto-exiting an active
  // session. See offline-availability.md.
  //
  // Per [[selection-mode.spec]] §§ Auto-reset on navigation and
  // [[records-page.spec]] US-006 § "?isReport=true URL flow".
  // The report param is only meaningful on the two pages that pick items for
  // a report — Photos and the Issues list — so other pages (and an issue's own
  // page) don't end up in selection mode because a stale search param happens
  // to be in the URL.
  //
  // Follow-up: swap these regexes for a `useMatches()` membership check when
  // a nested records sub-route lands (eg. `/records/$recordId`). Today the
  // route tree is flat enough that the regexes are correct + simpler.
  const isOnRecordsPage = /\/records\/?($|\?)/.test(pathname);
  const isOnIssuesPage = /\/issues\/?$/.test(pathname);
  const startedFromReports =
    isReportSearch && (isOnRecordsPage || isOnIssuesPage);
  useEffect(() => {
    if (startedFromReports && isOnline) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsSelectionMode(true);
      return;
    }
    setIsSelectionMode(false);
    // The counters are also re-published by the owning page's own syncs
    // (`setSelectedCount(...)`, `setIsAllSelected(...)`). Resetting them here
    // too is redundant but harmless — a same-tick blip while the bar is hidden
    // anyway. Kept for invariant clarity: when isSelectionMode flips to false,
    // every counter in the provider matches an empty selection.
    setSelectedCount(0);
    setIsAllSelected(false);
    setIsSelectingAll(false);
  }, [pathname, startedFromReports, isOnline]);

  // Derived, not stored. On Photos it follows the URL param; on the Issues
  // list every session is a report session. Only meaningful while
  // `isSelectionMode` is also true; offline the whole mode is gated.
  const isReportMode = (isReportSearch && isOnRecordsPage) || isOnIssuesPage;

  const exitReportMode = useCallback(() => {
    exitSelectionMode();
    if (!startedFromReports) return;
    // Back to where the report was started. Replace, so Back from Reports
    // does not reopen an empty picker. See [[selection-mode.spec]] § Report
    // mode.
    if (projectId) {
      void navigate({
        to: "/projects/$projectId/reports",
        params: { projectId },
        replace: true,
      });
    } else {
      void navigate({ to: pathname, replace: true });
    }
  }, [exitSelectionMode, startedFromReports, projectId, navigate, pathname]);

  const registerDeleteAction = useCallback((fn: () => void) => {
    deleteRef.current = fn;
  }, []);
  const registerReportAction = useCallback((fn: () => void) => {
    reportRef.current = fn;
  }, []);
  const registerIssuesAction = useCallback((fn: () => void) => {
    issuesRef.current = fn;
  }, []);
  const registerShareAction = useCallback((fn: () => Promise<void>) => {
    shareRef.current = fn;
  }, []);
  const registerSelectAllAction = useCallback((fn: () => void) => {
    selectAllRef.current = fn;
  }, []);
  const registerUnselectAllAction = useCallback((fn: () => void) => {
    unselectAllRef.current = fn;
  }, []);

  const triggerDelete = useCallback(() => {
    deleteRef.current?.();
  }, []);
  const triggerReport = useCallback(() => {
    reportRef.current?.();
  }, []);
  const triggerIssues = useCallback(() => {
    issuesRef.current?.();
  }, []);
  const triggerSelectAll = useCallback(() => {
    selectAllRef.current?.();
  }, []);
  const triggerUnselectAll = useCallback(() => {
    unselectAllRef.current?.();
  }, []);

  const triggerShare = useCallback(() => {
    if (isSharing) return;
    const fn = shareRef.current;
    if (!fn) return;
    setIsSharing(true);
    fn().finally(() => setIsSharing(false));
  }, [isSharing]);

  return (
    <SelectionModeContext.Provider
      value={{
        isSelectionMode,
        isReportMode,
        startedFromReports,
        selectedCount,
        isAllSelected,
        isSelectingAll,
        isDeletingRecords,
        isSharing,
        enterSelectionMode,
        exitSelectionMode,
        exitReportMode,
        setSelectedCount,
        setIsAllSelected,
        setIsSelectingAll,
        setIsDeletingRecords,
        registerDeleteAction,
        registerReportAction,
        registerIssuesAction,
        registerShareAction,
        registerSelectAllAction,
        registerUnselectAllAction,
        triggerDelete,
        triggerReport,
        triggerIssues,
        triggerShare,
        triggerSelectAll,
        triggerUnselectAll,
      }}
    >
      {children}
    </SelectionModeContext.Provider>
  );
}

export function useSelectionMode(): SelectionModeContextValue {
  const ctx = useContext(SelectionModeContext);
  if (!ctx) {
    throw new Error(
      "useSelectionMode must be used within a SelectionModeProvider",
    );
  }
  return ctx;
}
